import cloneDeep from 'clone-deep'
import { ClosingCostsMap } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { addClosingCosts, getElectiveClosingCosts } from 'services'
import { Modal, Select } from 'stories/components'
import { getPrice2decimal } from 'utils'

export type UnmatchedFee = {
  name: string | null
  amount: number | null
  notes: string | null
}

type ElectiveClosingCost = {
  lineId: number
  hudNo: number
  lineName: string | null
  type?: number | string
}

type Props = {
  unmatched: UnmatchedFee[]
  templateNo?: number
  existingLineIds?: number[]
  onClose: (saved: boolean) => void
  /**
   * Called after fees are added and page data is ready to receive amounts.
   * Parent should reload worksheet data, then apply these borrower amounts by lineId.
   */
  onAdded: (mappings: Array<{ lineId: number; amount: number | null; name: string | null }>) => Promise<void> | void
}

const normalize = (s: string | null | undefined) => (s || '').trim().toLowerCase()

const electiveLabel = (e: ElectiveClosingCost) => {
  const name = e.lineName || ClosingCostsMap[String(e.type)] || ClosingCostsMap[e.type as any] || 'Fee'
  return `${name} (HUD ${e.hudNo} / ID ${e.lineId})`
}

const findElectiveMatch = (fee: UnmatchedFee, electives: ElectiveClosingCost[]) => {
  const candidates = [fee.name, fee.notes].map(normalize).filter(Boolean)
  if (!candidates.length) return null
  for (const c of candidates) {
    const exact = electives.find((e) => {
      const n = normalize(e.lineName) || normalize(ClosingCostsMap[String(e.type)])
      return n === c
    })
    if (exact) return exact.lineId
  }
  for (const c of candidates) {
    const partial = electives.find((e) => {
      const n = normalize(e.lineName) || normalize(ClosingCostsMap[String(e.type)])
      return n && (n.includes(c) || c.includes(n))
    })
    if (partial) return partial.lineId
  }
  return null
}

export const AddUnmatchedFeesModal = ({ unmatched, templateNo, existingLineIds = [], onClose, onAdded }: Props) => {
  const [electives, setElectives] = useState<ElectiveClosingCost[]>([])
  const [selections, setSelections] = useState<Record<number, string>>({})
  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    ;(async () => {
      const data = await getElectiveClosingCosts(templateNo)
      const existing = new Set(existingLineIds.map((v) => Number(v)))
      const list: ElectiveClosingCost[] = (Array.isArray(data) ? data : []).filter(
        (item) => !existing.has(Number(item.lineId)),
      )
      setElectives(list)
      const initial: Record<number, string> = {}
      unmatched.forEach((fee, idx) => {
        const match = findElectiveMatch(fee, list)
        if (match != null) initial[idx] = String(match)
      })
      setSelections(initial)
      setReady(true)
    })()
  }, [templateNo, unmatched, existingLineIds.join(',')])

  const options = useMemo(() => {
    const opts: Record<string, string> = {}
    electives.forEach((e) => {
      opts[String(e.lineId)] = electiveLabel(e)
    })
    return opts
  }, [electives])

  const selectedLineIds = Object.values(selections).filter(Boolean)
  const hasDuplicateSelection = selectedLineIds.length !== new Set(selectedLineIds).size
  const allMapped = unmatched.length > 0 && unmatched.every((_, idx) => !!selections[idx])

  const onSelect = (idx: number, lineId: string) => {
    const temp = cloneDeep(selections)
    if (!lineId) delete temp[idx]
    else temp[idx] = lineId
    setSelections(temp)
  }

  const onModalClose = async (save: boolean) => {
    if (!save) {
      onClose(false)
      return
    }
    if (!allMapped) {
      if (!electives.length) {
        toast('No available fee lines to map. Existing fee lines are already on this loan.', { type: 'warning' })
        return
      }
      toast('Select a fee worksheet line for each unmatched fee.', { type: 'warning' })
      return
    }
    if (hasDuplicateSelection) {
      toast('Each unmatched fee must map to a different fee line.', { type: 'warning' })
      return
    }

    setLoading(true)
    try {
      const mappings = unmatched.map((fee, idx) => ({
        lineId: Number(selections[idx]),
        amount: fee.amount,
        name: fee.name || fee.notes,
      }))
      await addClosingCosts(
        mappings.map((m) => m.lineId),
        templateNo,
      )
      await onAdded(mappings)
      onClose(true)
    } catch {
      toast('Failed to add unmatched fees.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (!ready) return null

  return (
    <Modal
      title="Add unmatched fees"
      loading={loading}
      isOpen
      onClose={() => onModalClose(false)}
      onOk={() => onModalClose(true)}
      disabled={!allMapped || hasDuplicateSelection}
      titleOkay="Add Fees"
    >
      <div className="w-[720px] max-w-full space-y-3">
        <p className="text-sm text-gray-600">
          Map each unmatched fee from the PDF to a fee from the catalog (same list as “Add new fee”), then add them to
          the worksheet with the extracted amounts.
        </p>
        {existingLineIds.length > 0 && (
          <p className="text-sm text-amber-700">
            Existing fee lines are excluded from this list to avoid duplicate fee counting.
          </p>
        )}
        {hasDuplicateSelection && (
          <p className="text-sm text-amber-700">Each unmatched fee must use a different catalog fee.</p>
        )}
        <div className="overflow-auto border rounded max-h-[60vh]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left sticky top-0">
              <tr>
                <th className="p-2">From PDF</th>
                <th className="p-2 text-right">Amount</th>
                <th className="p-2 min-w-[280px]">Add as (catalog fee)</th>
              </tr>
            </thead>
            <tbody>
              {unmatched.map((fee, idx) => (
                <tr key={idx} className="border-t align-top">
                  <td className="p-2">
                    <div className="font-medium">{fee.name || fee.notes || 'Unnamed fee'}</div>
                    {fee.name && fee.notes && <div className="text-xs text-gray-500 mt-0.5">{fee.notes}</div>}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap">
                    {fee.amount != null ? getPrice2decimal(fee.amount, false, true) : '—'}
                  </td>
                  <td className="p-2">
                    <Select
                      id={`unmatched-fee-${idx}`}
                      title=""
                      value={selections[idx] || ''}
                      options={options}
                      hasDefaultOption
                      defaultOptionText="- Select fee -"
                      onChange={(v) => onSelect(idx, v)}
                      className="mb-0"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  )
}
